import { create } from "zustand";
import { nanoid } from "nanoid";
import { postGetEventsApply } from "../api/events";
import { useUserStore } from "./user";
import { formatDistanceToNow, Locale } from "date-fns";
import { ru, enUS, de, es, zhCN, ko } from "date-fns/locale";
import { TFunction } from "i18next";
import WebApp from "@twa-dev/sdk";

type SupportedLang = "en" | "ru" | "de" | "es" | "zh" | "ko";

const localeMap: Record<SupportedLang, Locale> = {
  en: enUS,
  ru,
  de,
  es,
  zh: zhCN,
  ko,
};

export const getTimeAgo = (
  date: Date | string,
  lang: SupportedLang = "en"
): string => {
  const targetDate = typeof date === "string" ? new Date(date) : date;

  return formatDistanceToNow(targetDate, {
    addSuffix: true,
    locale: localeMap[lang],
  });
};

interface ToastItem {
  id: string | number;
  title: string;
  timeAgo?: string;
  message?: string;
  duration?: number; // ms
}

interface ToasterStore {
  toasts: ToastItem[];
  addToast: (toast: Omit<ToastItem, "id">) => void;
  removeToast: (id: string | number) => void;
  removeToasts: () => void;
  addToasts: (
    t: TFunction<"translation", undefined>,
    locale: string
  ) => Promise<void>;
  startPolling: (
    t: TFunction<"translation", undefined>,
    locale: string
  ) => void;
  stopPolling: () => void;
}

const seenEvents = new Set<string>();

let pollingInterval: NodeJS.Timeout | null = null;

export const useToasterStore = create<ToasterStore>((set, get) => ({
  toasts: [],
  addToast: (toast) => {
    const id = nanoid();

    set((state) => ({
      toasts: [...state.toasts, { id, ...toast }],
    }));

    if (toast.duration !== 0) {
      setTimeout(() => {
        set((state) => ({
          toasts: state.toasts.filter((t) => t.id !== id),
        }));
      }, toast.duration || 5000);
    }
  },
  removeToast: (id) => {
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    }));
  },
  removeToasts: () => {
    set({
      toasts: [],
    });
  },
  addToasts: async (t, locale) => {
    const { user, setLastEventNow } = useUserStore.getState();

    const lastShownEventCreatedDate = user?.lastEventAt ?? null;

    let offset = 0;
    const limit = 50;

    let newToastAdded = false;

    // while (true) {
    const events = await postGetEventsApply({
      offset,
      limit,
      lastShownEventCreatedDate,
    });

    if (!events || events.length === 0) return;

    for (const event of events) {
      const uniqueId = `${event.type}-${event.createdAt}-${event.details.id}`;

      if (seenEvents.has(uniqueId)) continue;

      if (event.type === "wtt-bonus") continue;

      seenEvents.add(uniqueId);
      newToastAdded = true;

      const amount =
        //   event.type === "wtt-bonus" && "usdAmount" in event.details
        //     ? event.details.usdAmount ?? 0
        //     :
        "amount" in event.details
          ? event.details.amount
          : "tokenAmount" in event.details
          ? event.details.tokenAmount
          : "tonAmount" in event.details
          ? event.details.tonAmount ?? 0
          : 0;

      const token =
        "network" in event.details && event.details.network === "EVM"
          ? "BTN"
          : "tokenAmount" in event.details || "amount" in event.details
          ? "BTN"
          : "TON";

      const title =
        event.type === "withdrawal" &&
        "toAddress" in event.details &&
        !("fromAddress" in event.details)
          ? t(`events.withdrawal-${event.details.type}.title`)
          : t(`events.${event.type}.title`);

      get().addToast({
        title: title,
        timeAgo: getTimeAgo(event.createdAt, locale as SupportedLang),
        message: t(`events.${event.type}.message`, {
          amount: amount,
          token: token,
        }),
        duration: 0,
      });
      //   }

      if (events.length < limit) break;

      offset += limit;
    }

    await postGetEventsApply({
      offset,
      limit,
      lastShownEventCreatedDate: new Date().toISOString(),
    });

    if (newToastAdded) {
      WebApp.HapticFeedback.notificationOccurred("success");
    }

    setLastEventNow();
  },
  startPolling: (t, locale) => {
    // if (pollingInterval) return;
    const { addToasts } = get();
    addToasts(t, locale);
    // pollingInterval = setInterval(() => {
    //   addToasts(t, locale);
    // }, 15000);
  },
  stopPolling: () => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      pollingInterval = null;
    }
  },
}));
